'use client';

import ImageFullWidth from '@/components/blocks/image-full-width/image-full-width';
import ImageGrid from '@/components/blocks/image-grid/image-grid';
import TextLeft from '@/components/blocks/text/text-left';
import TextRight from '@/components/blocks/text/text-right';
import { ImageCarousel } from '@/components/sections/about/team/image-carousel/image-carousel';
import CaseCredit from '@/components/sections/cases/cases-detail/case-credit';
import H1 from '@/components/typography/h1';
import H3 from '@/components/typography/h3';
import H5 from '@/components/typography/h5';
import Label from '@/components/typography/label';
import { cn } from '@/lib/utils';
import { Case, Media } from '@/payload-types';
import { Dot } from 'lucide-react';
import Image from 'next/image';

type CaseDetailProps = {
    caseData: Case;
};

export default function CaseDetail({ caseData }: CaseDetailProps) {
    function convertToSlides(
        images: Array<{ afbeelding: string | Media; id?: string | null }>,
    ) {
        return images
            .filter((img) => !!img.afbeelding)
            .map((img) => ({
                src:
                    typeof img.afbeelding === 'string'
                        ? img.afbeelding
                        : img.afbeelding.url || '',
                id: img.id ? String(img.id) : '',
            }));
    }

    return (
        <div>
            <div className="mt-[54px] lg:mt-[77px]">
                <Image
                    src={
                        typeof caseData.banner === 'string'
                            ? '/placeholder.svg'
                            : caseData.banner?.url || '/placeholder.svg'
                    }
                    alt={
                        typeof caseData.banner === 'string'
                            ? ''
                            : caseData.banner.alt
                    }
                    width={3760}
                    height={1300}
                    className="bg-accent h-[405px] w-full object-cover lg:h-[860px]"
                />
                <div className="absolute top-0 flex w-full justify-center lg:top-[6rem] lg:pr-12">
                    <div className="mt-12 flex flex-col items-center pt-2 xl:mt-[6rem] xl:flex-row-reverse xl:justify-center xl:gap-12 xl:pl-[4rem]">
                        <div
                            className={cn(
                                `flex w-[18rem] transform-[rotate(-2.5deg)] flex-col rounded-lg sm:w-[22rem] lg:min-h-auto lg:w-[58.125rem]`,
                            )}
                            style={{
                                backgroundColor: caseData.color || '#FFFFFF',
                            }}
                        >
                            <div className="w-full border-b border-black">
                                <div className="flex items-center px-5 py-7 lg:py-12">
                                    <H3 className="mx-auto text-center italic lg:hidden">
                                        {caseData.titel.toUpperCase()}
                                    </H3>
                                    <H1
                                        variant="italic"
                                        className="mx-auto hidden text-center lg:block"
                                    >
                                        {caseData.titel.toUpperCase()}
                                    </H1>
                                </div>
                            </div>

                            <div className="border-primary flex border-b">
                                <div className="border-primary flex w-[46%] items-center border-r px-3 py-2 pl-5 xl:px-28 xl:py-2.5">
                                    <H5>Klant</H5>
                                </div>
                                <div className="flex w-full px-2 py-2 lg:px-5 xl:w-1/2 xl:py-2.5">
                                    <H5 className="normal-case">
                                        {caseData.algemeneInformatie.klant}
                                    </H5>
                                </div>
                            </div>
                            <div className="border-primary flex border-b">
                                <div className="border-primary flex w-[46%] items-center border-r px-3 py-2 pl-5 xl:px-28 xl:py-2.5">
                                    <H5>Inhoud</H5>
                                </div>
                                <div className="flex w-full px-2 py-2 lg:px-5 xl:w-1/2 xl:py-2.5">
                                    <H5 className="normal-case">
                                        {caseData.algemeneInformatie.inhoud}
                                    </H5>
                                </div>
                            </div>
                            <div className="border-primary flex border-b">
                                <div className="border-primary flex w-[46%] items-center border-r px-3 py-2 pl-5 xl:px-28 xl:py-2.5">
                                    <H5>Jaar</H5>
                                </div>
                                <div className="flex w-full px-2 py-2 lg:px-5 xl:w-1/2 xl:py-2.5">
                                    <H5 className="normal-case">
                                        {caseData.algemeneInformatie.jaar}
                                    </H5>
                                </div>
                            </div>

                            <div className="flex w-full justify-center py-5">
                                {caseData.algemeneInformatie.diensten.map(
                                    (item) => (
                                        <div
                                            className="flex items-center px-3 lg:px-6"
                                            key={item.id}
                                        >
                                            <Dot
                                                className="h-6 w-6 scale-[2.5] lg:h-8 lg:w-8"
                                                size={10}
                                                strokeWidth={2}
                                            />
                                            <Label>
                                                {typeof item.dienst ===
                                                    'object' &&
                                                'naam' in item.dienst
                                                    ? item.dienst.naam
                                                    : item.dienst}
                                            </Label>
                                        </div>
                                    ),
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="border-primary my-20 flex flex-col gap-20 lg:my-40 lg:gap-40">
                {caseData.content.map((block, index) => {
                    switch (block.blockType) {
                        case 'image-carousel':
                            const slides = convertToSlides(block.afbeeldingen);
                            if (slides.length > 0) {
                                return (
                                    <ImageCarousel
                                        key={index}
                                        height={{
                                            mobile: 300,
                                            desktop: 670,
                                        }}
                                        slides={slides}
                                    />
                                );
                            }
                            return null;
                        case 'text-right':
                            return (
                                <TextRight
                                    className="max-w-[769px] pr-[29px]"
                                    key={index}
                                    titel={block.titel}
                                    tekst={block.tekst}
                                    bijlage={block.bijlage}
                                />
                            );
                        case 'text-left':
                            return (
                                <TextLeft
                                    className="max-w-[769px] pr-[29px]"
                                    key={index}
                                    titel={block.titel}
                                    tekst={block.tekst}
                                    bijlage={block.bijlage}
                                />
                            );
                        case 'image-grid-2x2':
                            return (
                                <ImageGrid
                                    key={index}
                                    images={[
                                        { afbeelding: block.afbeelding1 },
                                        { afbeelding: block.afbeelding2 },
                                        { afbeelding: block.afbeelding3 },
                                        { afbeelding: block.afbeelding4 },
                                    ]}
                                />
                            );
                        case 'image-grid-2x1':
                            return (
                                <ImageGrid
                                    key={index}
                                    images={[
                                        { afbeelding: block.afbeelding1 },
                                        { afbeelding: block.afbeelding2 },
                                    ]}
                                />
                            );
                        case 'image-full-width':
                            return (
                                <ImageFullWidth
                                    key={index}
                                    afbeelding={block.afbeelding}
                                />
                            );
                        default:
                            return null;
                    }
                })}
                {caseData.credits && caseData.credits.length > 0 && (
                    <CaseCredit credits={caseData.credits ?? []} />
                )}
            </div>
        </div>
    );
}
